<template>
  <template v-if="hasPreview">
    <div class="mt-4 mb-2 px-4 flex items-center justify-between">
      <div class="flex items-center">
        <slot name="left" />
      </div>
      <div>
        <slot name="right" />
      </div>
    </div>

    <slot name="center" />

    <div class="grid grid-cols-[1fr_auto] max-w-[100vw] overflow-hidden pr-4">
      <div
        class="flex-1 flex gap-2 px-4 whitespace-nowrap overflow-hidden mr-4"
      >
        <div
          v-for="c in computedTag"
          :key="c.id"
          :class="[
            'badge badge-neutral',
            selected.has(c.id) ? 'badge-primary' : 'badge-neutral',
          ]"
          @click="onTagClick(c)"
        >
          {{ c.displayText }}
        </div>
      </div>
      <div class="text-[#ABABAB] cursor-pointer" @click="onTriggerPromptEdit">
        {{ $t("Buttons.viewAll") }}
      </div>
    </div>
  </template>
  <template v-if="hasModal">
    <VanPopup
      v-model:show="showEditor"
      class="h-full w-full m-0 grid grid-rows-[auto_1fr] overflow-hidden"
      position="left"
      safe-area-inset-top
      teleport="body"
      title="Clothing"
    >
      <ThreadsNav
        :go-back="() => (showEditor = false)"
        :show-center="false"
        show-back
      >
      </ThreadsNav>
      <edit-prompts3
        :mode="mode"
        :prompt="prompt"
        :negative-prompt="negativePrompt"
        :is-unlimited="isUnlimited"
        @update:negative-prompt="emit('update:negativePrompt', $event)"
        @update:prompt="emit('update:prompt', $event)"
        class="overflow-hidden"
      />
    </VanPopup>
  </template>
  <edit-prompts3
    v-else
    :mode="mode"
    :prompt="prompt"
    :negative-prompt="negativePrompt"
    :is-unlimited="isUnlimited"
    @update:negative-prompt="emit('update:negativePrompt', $event)"
    @update:prompt="emit('update:prompt', $event)"
    class="overflow-hidden"
  />
</template>

<script lang="ts" setup>
import { ImagePromptItem, usePrompts } from "@/stores/prompts";
import { PropType, computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { getRegByTagText, trim } from "@/modules/common";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import {
  getPoseNsfwTag,
  getClothingNsfwTag,
  getOtherNsfwTag,
} from "@/tools/nsfwPrompt";

const promptStore = usePrompts();
const { faceExpressionPrompts, posePrompts, clothingPrompts } =
  storeToRefs(promptStore);

let firstChildrenList = computed<ImagePromptItem[]>(() => {
  switch (props.mode) {
    case "clothing":
      return clothingPrompts.value[0].children;
    case "pose":
      return posePrompts.value[0].children;
    case "faceExpression":
      return faceExpressionPrompts.value;
    default:
      return [];
  }
});
const props = defineProps({
  mode: {
    type: String as PropType<
      "style" | "character" | Painting | "face" | "other"
    >,
    required: true,
  },
  prompt: {
    type: String,
    required: true,
  },
  negativePrompt: {
    type: String,
    required: false,
  },
  hasPreview: {
    type: Boolean,
    default: true,
  },
  hasModal: {
    type: Boolean,
    default: true,
  },
  hasNegative: {
    type: Boolean,
    default: true,
  },
  showModal: {
    type: Boolean,
    default: undefined,
  },
  isUnlimited: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:prompt",
  "update:negativePrompt",
  "update:showModal",
]);

const { t } = useI18n();

const _showEditor = ref(false);
const showEditor = computed({
  get() {
    if (props.showModal !== undefined) {
      if (props.showModal === true) initPrompt();

      return props.showModal;
    }
    return _showEditor.value;
  },
  set(val: boolean) {
    if (props.showModal !== undefined) emit("update:showModal", val);
    _showEditor.value = val;
  },
});
const selected = ref(new Map<number, ImagePromptItem>());
let positiveArrCache: ImagePromptItem[] = [];

const computedTag = computed(() => {
  if (props.isUnlimited) {
    if (props.mode === "pose") return getPoseNsfwTag().children;
    if (props.mode === "clothing") return getClothingNsfwTag().children;
    if (props.mode === "other") return getOtherNsfwTag().children;
  }
  let arr: ImagePromptItem[] = [];
  arr.push(...selected.value.values());
  if (arr.length >= 10) return arr.slice(0, 10);

  firstChildrenList.value.some((v) => {
    if (arr.length >= 10) return true;
    !selected.value.has(v.id) && arr.push(v);
  });
  return arr;
});

const onTagClick = (tag: ImagePromptItem) => {
  if (selected.value.has(tag.id)) {
    selected.value.delete(tag.id);
    positiveArrCache = positiveArrCache.filter((item) => item.id !== tag.id);
    emit("update:prompt", props.prompt.replace(getRegByTagText(tag.text), ""));
  } else {
    selected.value.set(tag.id, tag);
    positiveArrCache.push(tag);
    let prompt = trim(props.prompt, ",");
    prompt = prompt ? prompt + "," : "";
    prompt += tag.text + ",";
    emit("update:prompt", prompt);
  }
};

const onTriggerPromptEdit = () => {
  showEditor.value = true;
  initPrompt();
};

const initPrompt = () => {
  initSelected();
};
const initSelected = () => {
  const positiveList = promptStore.getPromptListByText(props.prompt);
  selected.value.clear();
  positiveList.forEach((v) => selected.value.set(v.id, v));
};

watch(() => props.prompt, initPrompt);

onMounted(() => {
  initSelected();
  if (!props.hasModal) initPrompt();
});
</script>
